// Brand Console DATA -- Search (real DataForSEO organic-ranking data).
//
// Consumes ONLY the real connector -> intelligence_bridge ->
// intelligence_measurements pipeline (connectors/dataforseo.py,
// connectors/intelligence_bridge.py's ingest_seo_performance_record /
// SEO_METRIC_SPECS) via the existing GET
// /intelligence/{workspace_id}/measurements route (entity_type=keyword,
// source_type=CONNECTOR), the same way PaidMediaPage.js/EcommercePage.js
// already consume their own provider's slice of the same pipeline.
// Deliberately does NOT call any legacy fabricated SEO route
// (/seo/audits/*, /seo/keywords/*, /dataforseo/keywords/*'s own mock
// fallback, /connections/dataforseo/test's own "Mock result") -- those
// are confirmed separate, non-real namespaces (see connectors/dataforseo.py's
// own module docstring), never wired into this page.
//
// CONNECTION-STATE ARCHITECTURE (verified by direct read, not assumed):
// unlike Shopify/Meta/Google Ads, DataForSEO is registered in server.py's
// own INFRASTRUCTURE_KEYS list -- a single, shared, platform-level
// credential resolved from the DATAFORSEO_API_KEY environment variable
// the SAME way for every tenant (get_vault_key's own INFRASTRUCTURE_KEYS
// branch), not a per-tenant vault entry a brand can paste in. Because of
// that, DataConnectionsPage.js's ConnectDialog never lists "dataforseo"
// as a provider, and it structurally cannot: there is no db.connectors
// document, no CredentialReference, no per-tenant "Connect" step for this
// provider to reuse. Inventing one here would be exactly the fabricated
// connection flow this task forbids. The real, already-existing
// mechanism this page exposes instead is the pair of real routes
// Milestone 52 already shipped with zero frontend consumer until now:
// GET /connections/dataforseo/status (is the shared platform key
// configured, plus real spend tracking) and POST
// /connections/dataforseo/sync/{client_id} (one real, live organic-SERP
// check for one real keyword against this client's own real website,
// persisted as a real measurement). The inline "Run a search check" form
// below is the smallest safe UI for that second route -- not a connect
// flow, just the one real action a brand can actually take here.

import { useState, useEffect, useCallback, useMemo } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { EmptyState, ErrorState, LoadingState } from '@/components/brand/StateViews';
import { StatusBadge } from '@/components/brand/Badges';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { dataforseoAPI } from '@/lib/api';
import { measurementsAPI, entitiesAPI } from '@/lib/intelligenceApi';
import { WorkspaceSelect } from '@/components/brand/BrandContextBar';
import { useWorkspaceSelection } from '@/hooks/useWorkspaceSelection';
import { toast } from 'sonner';

function dash(v) {
  return v === null || v === undefined || v === '' ? '—' : v;
}
function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}
function formatNumber(n, digits = 0) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return n.toLocaleString(undefined, { maximumFractionDigits: digits });
}
function providerOf(measurement) {
  return (measurement.source_reference || '').split(':')[0];
}
// The real DataForSEO source_reference shape (connectors/intelligence_
// bridge.py's _create_measurements, verified directly):
// "dataforseo:<record_type>:<keyword>|<location_code>|<language_code>".
// Parsed defensively -- any shape mismatch honestly falls back to '—'
// rather than guessing a value.
function parseLocationLanguage(sourceReference) {
  const parts = (sourceReference || '').split(':');
  if (parts.length < 3) return { locationCode: null, languageCode: null };
  const objectId = parts.slice(2).join(':');
  const segments = objectId.split('|');
  if (segments.length !== 3) return { locationCode: null, languageCode: null };
  return { locationCode: segments[1] || null, languageCode: segments[2] || null };
}

function sortRows(rows, key, dir) {
  const copy = [...rows];
  copy.sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    if (av === bv) return 0;
    if (av === null || av === undefined) return 1;
    if (bv === null || bv === undefined) return -1;
    const cmp = av < bv ? -1 : 1;
    return dir === 'asc' ? cmp : -cmp;
  });
  return copy;
}

function SortHeader({ label, col, sort, onSort, testId }) {
  return (
    <th className="px-3 py-2 cursor-pointer select-none whitespace-nowrap" onClick={() => onSort(col)} data-testid={testId}>
      {label}{sort.key === col ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );
}

function RunCheckForm({ clientId, onChecked }) {
  const [keyword, setKeyword] = useState('');
  const [locationCode, setLocationCode] = useState('2840');
  const [languageCode, setLanguageCode] = useState('en');
  const [running, setRunning] = useState(false);

  const run = async (e) => {
    e.preventDefault();
    if (!keyword.trim()) { toast.error('A real keyword is required'); return; }
    setRunning(true);
    try {
      await dataforseoAPI.syncKeyword(clientId, {
        keyword: keyword.trim(),
        location_code: locationCode ? Number(locationCode) : undefined,
        language_code: languageCode || undefined,
      });
      toast.success(`Checked "${keyword.trim()}" -- a real ranking observation was recorded`);
      setKeyword('');
      onChecked();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'DataForSEO check failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <form onSubmit={run} className="flex items-end gap-2 flex-wrap" data-testid="dataforseo-run-check-form">
      <div>
        <label className="block text-[10px] text-[var(--text-tertiary)] mb-1">Keyword</label>
        <Input data-testid="dataforseo-keyword-input" className="h-8 w-[200px] text-[12px]" value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="e.g. project management software" />
      </div>
      <div>
        <label className="block text-[10px] text-[var(--text-tertiary)] mb-1">Location code</label>
        <Input data-testid="dataforseo-location-input" type="number" className="h-8 w-[110px] text-[12px]" value={locationCode} onChange={(e) => setLocationCode(e.target.value)} placeholder="2840 = US" />
      </div>
      <div>
        <label className="block text-[10px] text-[var(--text-tertiary)] mb-1">Language</label>
        <Input data-testid="dataforseo-language-input" className="h-8 w-[90px] text-[12px]" value={languageCode} onChange={(e) => setLanguageCode(e.target.value)} placeholder="en" />
      </div>
      <Button type="submit" size="sm" className="h-8 text-[12px]" disabled={running} data-testid="dataforseo-run-check-button">
        {running ? 'Checking…' : 'Run a search check'}
      </Button>
    </form>
  );
}

export default function SearchDataPage() {
  // P1.9: client resolution is now the shared useWorkspaceSelection() hook
  // (see SignalsPage.js's own comment for the full rationale) instead of
  // an unswitchable first-client fetch.
  const { clients, workspaceId, setWorkspaceId, loadingClients } = useWorkspaceSelection();
  const clientId = workspaceId || null;
  const noClient = !loadingClients && clients.length === 0;
  const [dfStatus, setDfStatus] = useState(null); // null = loading
  const [measurements, setMeasurements] = useState(null); // null = loading
  const [entityNames, setEntityNames] = useState({});
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({ entity_id: '', status: '' });
  const [appliedFilters, setAppliedFilters] = useState({ entity_id: '', status: '' });

  const [sort, setSort] = useState({ key: 'observed_at', dir: 'desc' });
  const [selected, setSelected] = useState(null);

  const load = useCallback((activeFilters) => {
    if (!clientId) return;
    setDfStatus(null); setMeasurements(null); setError(null);
    const params = { entity_type: 'keyword', source_type: 'CONNECTOR' };
    if (activeFilters.entity_id) params.entity_id = activeFilters.entity_id;
    if (activeFilters.status) params.status = activeFilters.status;
    Promise.all([
      dataforseoAPI.status(),
      measurementsAPI.listForWorkspace(clientId, params),
      entitiesAPI.list(clientId, { entityType: 'keyword' }).catch(() => ({ data: { entities: [] } })),
    ])
      .then(([statusRes, measRes, entRes]) => {
        setDfStatus(statusRes.data || { connected: false });
        // Not every entity_type=keyword CONNECTOR measurement is
        // necessarily DataForSEO's (Section E of the audit: "do not
        // assume every measurement is SEO data") -- filtered again here
        // by real source_reference provider, exactly like PaidMediaPage/
        // EcommercePage's own provider filter.
        const seoRecords = (measRes.data?.measurements || []).filter((m) => providerOf(m) === 'dataforseo');
        setMeasurements(seoRecords);
        const names = {};
        (entRes.data?.entities || entRes.data || []).forEach((e) => { names[e.id] = e.canonical_name || e.id; });
        setEntityNames(names);
      })
      .catch((e) => setError(e));
  }, [clientId]);

  useEffect(() => { load(appliedFilters); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyFilters = (e) => {
    e.preventDefault();
    setAppliedFilters(filters);
    load(filters);
  };
  const clearFilters = () => {
    const cleared = { entity_id: '', status: '' };
    setFilters(cleared);
    setAppliedFilters(cleared);
    load(cleared);
  };

  const positionRecords = useMemo(() => (measurements || []).filter((m) => m.metric_key === 'position'), [measurements]);
  // Final production-hardening audit finding: an INVALIDATED measurement
  // is the backend's own explicit "this record is no longer trustworthy"
  // state (MeasurementService.invalidate_measurement) -- summing/
  // averaging it into a real-looking Summary would silently misrepresent
  // real data as valid, exactly the bug already fixed in PaidMediaPage.js/
  // EcommercePage.js. The raw table below still shows every record,
  // INVALIDATED included (with its real status visible), for full
  // transparency; only the Summary excludes it.
  const validPositionRecords = useMemo(() => positionRecords.filter((m) => m.status !== 'INVALIDATED'), [positionRecords]);

  const summary = useMemo(() => {
    if (!measurements) return null;
    if (validPositionRecords.length === 0) return { keywordsTracked: 0, avgPosition: null, bestPosition: null, worstPosition: null };
    const values = validPositionRecords.map((m) => m.value).filter((v) => v !== null && v !== undefined);
    const keywordsTracked = new Set(validPositionRecords.map((m) => m.entity_id)).size;
    return {
      keywordsTracked,
      avgPosition: values.length ? values.reduce((a, b) => a + b, 0) / values.length : null,
      bestPosition: values.length ? Math.min(...values) : null,
      worstPosition: values.length ? Math.max(...values) : null,
    };
  }, [measurements, validPositionRecords]);

  const sortedRows = useMemo(() => sortRows(positionRecords, sort.key, sort.dir), [positionRecords, sort]);

  const toggleSort = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }));
  };

  const loading = dfStatus === null || measurements === null;
  const connected = !!dfStatus?.connected;

  return (
    <DashboardLayout>
      <div data-testid="search-data-page" className="max-w-5xl mx-auto">
        <div className="mb-4">
          <h1 className="font-display text-[20px] font-bold text-[var(--text-primary)] mb-1">Search</h1>
          <p className="text-[12px] text-[var(--text-secondary)]">Real, DataForSEO-observed organic ranking checks -- never an estimated position or search volume.</p>
        </div>

        {!noClient && clients.length > 0 && (
          <div className="mb-4">
            <WorkspaceSelect clients={clients} workspaceId={workspaceId} setWorkspaceId={setWorkspaceId} />
          </div>
        )}

        {noClient && <EmptyState title="ADD A BRAND FIRST" description="Search data appears once Martian has a workspace with a real website on file." />}

        {!noClient && loading && !error && <LoadingState />}
        {!noClient && error && <ErrorState error={error} onRetry={() => load(appliedFilters)} />}

        {!noClient && !error && !loading && (
          <div className="space-y-6">
            {connected ? (
              <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-3" data-testid="dataforseo-connected-panel">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[11px] text-[var(--text-secondary)]">DataForSEO <StatusBadge value="CONNECTED" /></span>
                  {dfStatus?.today_spend !== undefined && (
                    <span className="text-[10px] font-mono text-[var(--text-tertiary)]">Today: ${dash(dfStatus.today_spend)} · Cap: ${dash(dfStatus.daily_cap)}/day</span>
                  )}
                </div>
                <RunCheckForm clientId={clientId} onChecked={() => load(appliedFilters)} />
              </div>
            ) : (
              <EmptyState
                testId="dataforseo-not-connected"
                title="Search ranking checks are unavailable"
                description="This page reports organic ranking checks from a separate search-data service. That service is not available right now. This is not a Google Search Console failure — Search Console data stays on Evidence and Watchlist. Martian will not show an estimated ranking or search volume in place of real data."
              />
            )}

            {positionRecords.length === 0 && connected && (
              <EmptyState
                testId="search-data-empty"
                title="No search data yet"
                description="DataForSEO is connected, but Martian hasn't checked any keywords for this workspace yet. Run a search check above to record the first real ranking observation."
              />
            )}
            {positionRecords.length === 0 && !connected && (
              <EmptyState
                testId="search-data-empty"
                title="No search data yet"
                description="Martian has no real keyword ranking checks recorded for this workspace."
              />
            )}

            {positionRecords.length > 0 && (
              <>
                <div>
                  <p className="text-[11px] font-mono uppercase tracking-wide text-[var(--text-tertiary)] mb-2">Summary</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="search-data-summary">
                    {[
                      ['Keywords tracked', summary.keywordsTracked, (v) => formatNumber(v)],
                      ['Average position', summary.avgPosition, (v) => formatNumber(v, 1)],
                      ['Best position', summary.bestPosition, (v) => formatNumber(v)],
                      ['Worst position', summary.worstPosition, (v) => formatNumber(v)],
                    ].map(([label, value, fmt]) => (
                      <div key={label} data-testid={`search-data-metric-${label.toLowerCase().replace(/\s+/g, '-')}`} className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-card)]">
                        <p className="text-[10px] font-mono uppercase tracking-wide text-[var(--text-tertiary)] mb-1.5">{label}</p>
                        {value === null || value === undefined ? (
                          <p className="text-[16px] font-semibold text-[var(--text-tertiary)]">—</p>
                        ) : (
                          <p className="text-[16px] font-semibold text-[var(--text-primary)]">{fmt(value)}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-[11px] font-mono uppercase tracking-wide text-[var(--text-tertiary)] mb-2">Keyword checks</p>
                  <form onSubmit={applyFilters} className="flex items-end gap-2 flex-wrap mb-3" data-testid="search-data-filters">
                    <div>
                      <label className="block text-[10px] text-[var(--text-tertiary)] mb-1">Entity ID</label>
                      <Input data-testid="filter-search-entity-id" className="h-8 w-[200px] text-[12px]" value={filters.entity_id} onChange={(e) => setFilters((f) => ({ ...f, entity_id: e.target.value }))} placeholder="keyword entity id" />
                    </div>
                    <div>
                      <label className="block text-[10px] text-[var(--text-tertiary)] mb-1">Status</label>
                      <Select value={filters.status || 'ANY'} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === 'ANY' ? '' : v }))}>
                        <SelectTrigger data-testid="filter-search-status" className="h-8 w-[130px] text-[12px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ANY">Any</SelectItem>
                          <SelectItem value="RECORDED">Recorded</SelectItem>
                          <SelectItem value="INVALIDATED">Invalidated</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button type="submit" size="sm" data-testid="apply-search-filters" className="h-8 text-[12px]">Apply</Button>
                    <Button type="button" variant="outline" size="sm" data-testid="clear-search-filters" className="h-8 text-[12px]" onClick={clearFilters}>Clear filters</Button>
                  </form>

                  <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] overflow-x-auto" data-testid="search-data-table">
                    <table className="w-full text-[11px]">
                      <thead>
                        <tr className="border-b border-[var(--border)] text-left text-[var(--text-tertiary)]">
                          <th className="px-3 py-2">Keyword</th>
                          <SortHeader label="Position" col="value" sort={sort} onSort={toggleSort} testId="sort-search-position" />
                          <th className="px-3 py-2">Location</th>
                          <th className="px-3 py-2">Language</th>
                          <SortHeader label="Observed at" col="observed_at" sort={sort} onSort={toggleSort} testId="sort-search-observed" />
                          <th className="px-3 py-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedRows.map((m) => {
                          const { locationCode, languageCode } = parseLocationLanguage(m.source_reference);
                          return (
                            <tr
                              key={m.id} data-testid={`search-row-${m.id}`}
                              className="border-b border-[var(--border-light,var(--border))] last:border-b-0 cursor-pointer hover:bg-[var(--surface-sunken,var(--canvas-secondary))] focus-within:bg-[var(--surface-sunken,var(--canvas-secondary))]"
                              onClick={() => setSelected(m)}
                              tabIndex={0}
                              onKeyDown={(e) => { if (e.key === 'Enter') setSelected(m); }}
                            >
                              <td className="px-3 py-2 text-[var(--text-primary)]">{dash(entityNames[m.entity_id] || m.entity_id)}</td>
                              <td className="px-3 py-2 text-[var(--text-primary)] font-mono">{dash(m.value)}</td>
                              <td className="px-3 py-2 text-[var(--text-secondary)] font-mono">{dash(locationCode)}</td>
                              <td className="px-3 py-2 text-[var(--text-secondary)]">{dash(languageCode)}</td>
                              <td className="px-3 py-2 text-[var(--text-secondary)] font-mono whitespace-nowrap">{formatDateTime(m.observed_at)}</td>
                              <td className="px-3 py-2">{m.status ? <StatusBadge value={m.status} /> : '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Search check detail ── */}
        <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
          <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto bg-[var(--surface)] border-[var(--border)]" data-testid="search-detail">
            {selected && (() => {
              const { locationCode, languageCode } = parseLocationLanguage(selected.source_reference);
              return (
                <>
                  <SheetHeader>
                    <SheetTitle className="text-[var(--text-primary)] text-[14px]">{dash(entityNames[selected.entity_id] || selected.entity_id)}</SheetTitle>
                    <SheetDescription className="text-[var(--text-tertiary)] text-[11px]">The real, stored ranking observation.</SheetDescription>
                  </SheetHeader>
                  <div className="mt-3 space-y-3 text-[12px]">
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                      <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Position</p><p className="text-[var(--text-primary)] font-mono" data-testid="detail-search-position">{dash(selected.value)}</p></div>
                      <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Target domain</p><p className="text-[var(--text-primary)] font-mono">{dash(selected.metadata?.target_domain)}</p></div>
                      <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Location code</p><p className="text-[var(--text-primary)]">{dash(locationCode)}</p></div>
                      <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Language</p><p className="text-[var(--text-primary)]">{dash(languageCode)}</p></div>
                      <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Observed at</p><p className="text-[var(--text-primary)]">{formatDateTime(selected.observed_at)}</p></div>
                      <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Status</p><p>{selected.status ? <StatusBadge value={selected.status} /> : '—'}</p></div>
                    </div>
                    <Separator className="bg-[var(--border)]" />
                    <div>
                      <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Source reference</p>
                      <p className="text-[var(--text-tertiary)] font-mono break-all" data-testid="detail-search-source">{dash(selected.source_reference)}</p>
                    </div>
                  </div>
                </>
              );
            })()}
          </SheetContent>
        </Sheet>
      </div>
    </DashboardLayout>
  );
}
